/**
 * Qué puede hacer ESTE móvil.
 *
 * La estrategia de subida (decisión D3) depende de esto, y la interfaz
 * también: **el copy cambia según lo que el dispositivo pueda cumplir**. En un
 * iPhone no se puede prometer que la subida siga con la app cerrada, porque
 * Safari no implementa Background Sync. Prometerlo y que no ocurra es peor que
 * no prometerlo: la empleada cierra la app confiada y la prueba se queda en el
 * móvil.
 *
 * Todo se detecta en tiempo de ejecución. Nada de mirar el user-agent para
 * decidir capacidades: los navegadores mienten y los sistemas cambian.
 *
 * Y solo se detecta lo que la aplicación **sabe hacer** con la respuesta.
 * Había dos que no: `notificaciones` (Web Push) y `nfc` (Web NFC). De ninguna
 * de las dos existe una línea de código —ni clave VAPID, ni suscripción, ni un
 * `NDEFReader` en ninguna pantalla— y sin embargo el catálogo tenía los textos
 * esperando: «Activar avisos de subida», «Te avisaremos si queda algo por subir
 * cuando cierres la app», «NFC activo · acerca el móvil a la etiqueta».
 *
 * Es el mismo montaje que la huella y el Face ID del paso 3 del alta: la
 * promesa entera menos la parte que la cumple. Y detectar la capacidad es justo
 * lo que hace que parezca «a medio hacer» en lugar de «no hecho», así que
 * invita a enchufar el texto a nada. Si algún día se hacen, la detección vuelve
 * con su consumidor.
 */

export interface Capacidades {
    /** ¿La cola puede seguir con la app cerrada? En iOS, no. */
    subidaEnSegundoPlano: boolean;
    /** Screen Wake Lock: mantener la pantalla encendida mientras sube. */
    puedeEvitarSuspension: boolean;
    /** ¿Está instalada en la pantalla de inicio? En iOS cambia los permisos. */
    instalada: boolean;
    /** Formato que graba este navegador. */
    formatoVideo: string | null;
    grabacion: boolean;
    camara: boolean;
    ubicacion: boolean;
    esIos: boolean;
}

const FORMATOS = [
    // El orden importa: mp4 primero porque es lo que graba iOS y lo que
    // reproduce todo. webm/vp9 es el de Android y comprime mejor.
    'video/mp4;codecs=avc1.42E01E',
    'video/mp4',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
];

export function formatoDeVideo(): string | null {
    if (typeof MediaRecorder === 'undefined') {
        return null;
    }

    return FORMATOS.find((f) => MediaRecorder.isTypeSupported(f)) ?? null;
}

export function detectar(): Capacidades {
    const nav = typeof navigator === 'undefined' ? undefined : navigator;

    const instalada =
        typeof window !== 'undefined' &&
        (window.matchMedia?.('(display-mode: standalone)').matches === true ||
            (nav as unknown as { standalone?: boolean })?.standalone === true);

    // Lo unico que se saca del user-agent, y solo para elegir el texto de
    // ayuda: "Ajustes -> Safari -> Camara" no vale en Android. Ninguna
    // capacidad se decide con esto; para eso estan las comprobaciones de
    // arriba, que preguntan por la funcion en lugar de adivinar el sistema.
    const ua = nav?.userAgent ?? '';
    const esIos =
        /iPad|iPhone|iPod/.test(ua) ||
        // Desde iPadOS 13 un iPad se presenta como un Mac. La pista es que
        // ningun Mac de verdad tiene pantalla tactil.
        (/Macintosh/.test(ua) && (nav?.maxTouchPoints ?? 0) > 1);

    const sync =
        typeof window !== 'undefined' &&
        'serviceWorker' in (nav ?? {}) &&
        'SyncManager' in window;

    return {
        subidaEnSegundoPlano: sync,
        puedeEvitarSuspension: typeof nav !== 'undefined' && 'wakeLock' in nav,
        instalada,
        formatoVideo: formatoDeVideo(),
        grabacion: typeof MediaRecorder !== 'undefined',
        camara: nav?.mediaDevices?.getUserMedia !== undefined,
        ubicacion: nav?.geolocation !== undefined,
        esIos,
    };
}

/**
 * El texto de la pantalla 07, elegido por lo que el móvil puede cumplir.
 *
 * Es la traducción literal de D3 a una línea de interfaz.
 */
export function claveDeCopySubida(
    cap: Capacidades,
    soloWifi: boolean,
    hayWifi: boolean
): 'resumen.subida_fondo' | 'resumen.subida_primer_plano' | 'resumen.subida_wifi' {
    if (soloWifi && !hayWifi) {
        return 'resumen.subida_wifi';
    }

    return cap.subidaEnSegundoPlano ? 'resumen.subida_fondo' : 'resumen.subida_primer_plano';
}
